/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * ToastContainer.tsx — Toas\1 \2ontainer (services/operator-web-app).
 */

/**
 * E3-S3 T3: Renders toasts from toastStore; auto-dismiss is scheduled in store.
 * Supports variant-based styling (success, error, warning, info) with slide-in animation.
 */
import { useStore } from 'zustand';
import { toastStore } from '@/store/toastStore';
import type { ToastVariant } from '@/store/toastStore';
import { useTranslation } from '@/i18n';

const variantStyles: Record<ToastVariant, { border: string; accent: string }> = {
  info: { border: 'border-blue-400/40', accent: 'bg-blue-500/10 text-blue-300' },
  success: { border: 'border-green-400/40', accent: 'bg-green-500/10 text-green-300' },
  error: { border: 'border-red-400/40', accent: 'bg-red-500/10 text-red-300' },
  warning: { border: 'border-amber-400/40', accent: 'bg-amber-500/10 text-amber-300' },
};

const variantIcons: Record<ToastVariant, string> = {
  info: '\u2139\uFE0F',
  success: '\u2705',
  error: '\u274C',
  warning: '\u26A0\uFE0F',
};

export function ToastContainer() {
  const t = useTranslation();
  const toasts = useStore(toastStore, (s) => s.toasts);
  const removeToast = useStore(toastStore, (s) => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm"
        role="region"
        aria-label={t('toast.notifications')}
      >
        {toasts.map((toast) => {
          const styles = variantStyles[toast.variant];
          return (
            <div
              key={toast.id}
              className={`glass-panel flex items-start gap-2 border ${styles.border} px-3 py-2 text-sm shadow-lg`}
              style={{
                animation: 'toast-slide-in 0.3s ease-out',
                color: 'var(--text-primary)',
              }}
              role="status"
            >
              <span className="shrink-0 text-base leading-5" aria-hidden="true">
                {variantIcons[toast.variant]}
              </span>
              <span className="flex-1">{toast.message}</span>
              <button
                onClick={() => removeToast(toast.id)}
                className="shrink-0 ml-1 opacity-60 hover:opacity-100 transition-opacity"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: 1,
                  padding: 0,
                }}
                aria-label={t('common.close')}
              >
                &times;
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
